import { CellStyle, ColDef, ColumnState, EditableCallbackParams } from 'ag-grid-community';
import { aggridHeaderClass, aggridSwitchButtonWrapperStyle } from 'src/components/common/common';
import { GridCustomColumnDefs } from 'src/utils/grid/GridCustomColumnDefs';
import { getSwitchButtonColumnOptions } from 'src/utils/grid/GridHandlerContent';
import { buildDefaultColumnStateFromDefs } from 'src/utils/grid/columnPinUtils';
import { getDefaultRowIndexColumnConfig } from 'src/utils/grid/gridStyleUtils';

import { SYS1010_R01Dto } from './Dto_SYS1010_R01';

export const COLUMN_STATE_KEY = 'SYS1010_R01-grid-column-state';
export const DETAIL_COLUMN_STATE_KEY = 'SYS1010_R01-minor-grid-column-state';

export const DETAIL_DEFAULT_PIN_COLS = [];

export const headerNames: Record<string, string> = {
	rowIndex: '',
	id: '공통코드',
	majorCd: '공통코드',
	codeNm: '코드명',
	sysFlg: '시스템여부',
	remark: '비고',
	ref01: '참조1',
	ref02: '참조2',
	ref03: '참조3',
	ref04: '참조4',
	ref05: '참조5',
	ref06: '참조6',
	ref07: '참조7',
	ref08: '참조8',
	ref09: '참조9',
	ref10: '참조10',
};

export const detailHeaderNames: Record<string, string> = {
	rowIndex: '',
	id: '상세코드',
	minorCd: '상세코드',
	codeNm: '코드명',
	sysFlg: '시스템여부',
	dispSeq: '순서',
	remark: '비고',
	ref01: '참조1',
	ref02: '참조2',
	ref03: '참조3',
	ref04: '참조4',
	ref05: '참조5',
	ref06: '참조6',
	ref07: '참조7',
	ref08: '참조8',
	ref09: '참조9',
	ref10: '참조10',
};

const refColsConfig = [
	{ headerName: '비고', colId: 'remark', field: 'remark' },
	...Array.from({ length: 10 }, (_, i) => ({
		headerName: `참조${i + 1}`,
		colId: `ref${(i + 1).toString().padStart(2, '0')}`,
		field: `ref${(i + 1).toString().padStart(2, '0')}`,
	})),
];

const detailRefColsConfig: {
	headerName: string;
	colId: string;
	field: string;
	editable: boolean;
	pasteable: boolean;
}[] = [
	{
		headerName: '비고', colId: 'remark', field: 'remark',
		editable: true,
		pasteable: true,
	},
	...Array.from({ length: 10 }, (_, i) => ({
		headerName: `참조${i + 1}`,
		colId: `ref${(i + 1).toString().padStart(2, '0')}`,
		field: `ref${(i + 1).toString().padStart(2, '0')}`,
		editable: true,
		pasteable: true,
	})),
];

const columnEditable = (params: { data?: SYS1010_R01Dto }) =>
	params.data?.inserted === true;

const detailColumnEditable = (params: EditableCallbackParams<SYS1010_R01Dto>) => {
	if (params.colDef.field === 'minorCd') {
		return params.data?.inserted === true;
	}
	return params.data?.inserted === true;
};

export type getCellStyle = (params: unknown, key: string) => CellStyle | undefined;

export interface CreateDetailBaseColumnDefsParams {
	indexCellRenderer: (params: SYS1010_R01Dto) => React.ReactNode;
	handleSwitchButtonHandler: (
		valueIndex: number,
		params: unknown,
		newValue: 'Y' | 'N',
		headerNames: Record<string, string>,
	) => void;
}

const createReferenceColumnDefs = (
	getCellStyle: getCellStyle,
): ColDef<SYS1010_R01Dto>[] =>
	refColsConfig.map((col) => ({
		headerName: col.headerName, colId: col.colId, field: col.field,
		headerClass: aggridHeaderClass,
		cellStyle: (p) => getCellStyle(p, col.field),
		editable: false,
	}) as ColDef<SYS1010_R01Dto>);

const createDetailReferenceColumnDefs = (): ColDef<SYS1010_R01Dto>[] =>
	detailRefColsConfig.map((col) => ({
		headerName: col.headerName, colId: col.colId, field: col.field,
		editable: col.editable,
		pasteable: col.pasteable,
	}) as ColDef<SYS1010_R01Dto>);

/**
 * 기본 컬럼 정의
 */
export const createBaseColumnDefs = (
	getCellStyle: getCellStyle, 
): ColDef<SYS1010_R01Dto>[] => {
	const referenceColumnDefs = createReferenceColumnDefs(getCellStyle);

	return [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: '공통코드', colId: 'majorCd', field: 'majorCd', width: 150,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'majorCd'),
			editable: columnEditable,
		},
		{
			headerName: '코드명', colId: 'codeNm', field: 'codeNm', width: 150,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'codeNm'),
			editable: false,
		},
		{
			headerName: '시스템여부', colId: 'sysFlg', field: 'sysFlg', width: 100,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'sysFlg'),
			editable: false,
		},
		...referenceColumnDefs,
	];
};

/**
 * Detail 부분 기본 컬럼 정의
 */
export const createDetailBaseColumnDefs = ({
	indexCellRenderer,
	handleSwitchButtonHandler,
}: CreateDetailBaseColumnDefsParams): ColDef<SYS1010_R01Dto>[] => [
	...getDefaultRowIndexColumnConfig(indexCellRenderer),
	{
		headerName: '상세코드', colId: 'minorCd', field: 'minorCd', width: 150,
		editable: detailColumnEditable,
		pasteable: true,
	} as ColDef<SYS1010_R01Dto>,
	{
		headerName: '코드명', colId: 'codeNm', field: 'codeNm', width: 150,
		editable: (params) => params.data !== undefined,
		headerClass: aggridHeaderClass,
		pasteable: true,
	} as ColDef<SYS1010_R01Dto>,
	{
		headerName: '시스템여부', colId: 'sysFlg', field: 'sysFlg', width: 100,
		...getSwitchButtonColumnOptions('sysFlg', handleSwitchButtonHandler, detailHeaderNames, aggridSwitchButtonWrapperStyle, true),
	},
	{
		headerName: '순서', colId: 'dispSeq', field: 'dispSeq', width: 150,
		editable: (params) => params.data !== undefined,
		headerClass: aggridHeaderClass,
		pasteable: true,
		valueParser: (params) =>
			Number(String(params.newValue ?? '').replace(/,/g, '').trim()) || 0,
	} as ColDef<SYS1010_R01Dto>,
	...createDetailReferenceColumnDefs(),
];

//기본 컬럼 상태 정의
export const defaultColumnState = (
	baseColumnDefs: ColDef<SYS1010_R01Dto>[],
) => buildDefaultColumnStateFromDefs(baseColumnDefs, []);

//Detail 부분 기본 컬럼 상태 정의
export const defaultDetailColumnState = (
	baseColumnDefs: ColDef<SYS1010_R01Dto>[],
) => buildDefaultColumnStateFromDefs(baseColumnDefs, DETAIL_DEFAULT_PIN_COLS);

/**
 * 최종 컬럼 정의 (컬럼 상태 병합 포함)
 */
export const creatColumnDefs = (
	baseColumnDefs: ColDef<SYS1010_R01Dto>[],
	columnState: ColumnState[] | null,
): ColDef<SYS1010_R01Dto>[] => {
	if (columnState === null) return baseColumnDefs;
	return GridCustomColumnDefs<SYS1010_R01Dto>({
		baseColumnDefs,
		columnState,
		headerNames: headerNames,
		fallbackHeaderClass: aggridHeaderClass,
	});
};

/**
 * Detail 부분 최종 컬럼 정의 (컬럼 상태 병합 포함)
 */
export const createDetailColumnDefs = (
	baseColumnDefs: ColDef<SYS1010_R01Dto>[],
	columnState: ColumnState[] | null,
): ColDef<SYS1010_R01Dto>[] => {
	if (columnState === null) return baseColumnDefs;
	return GridCustomColumnDefs<SYS1010_R01Dto>({
		baseColumnDefs,
		columnState,
		headerNames: detailHeaderNames,
		fallbackHeaderClass: aggridHeaderClass,
	});
};
